import { inject, Component, input, OnInit, output, ChangeDetectionStrategy, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { FormacaoService as FormacaoCoreService } from '@/core/api/sicom-api/registro/medico/formacao/formacao.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { forkJoin, finalize, of, catchError } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { FormacaoRequest } from '@/core/api/sicom-api/registro/medico/formacao/formacao.model';
import { PosGraduacaoResponse } from '@/core/api/sicom-api/registro/medico/formacao/pos-graduacao.model';
import { FormacaoListItem } from '@/features/registro/medicos/dashboard-medicos/components/formacao/formacao.component';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { TagModule } from 'primeng/tag';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface FormacaoListaRow {
    tipo: { value: string; color: string };
    instituicao: string;
    dataFormatura: string;
    source: FormacaoListItem;
}

@Component({
    selector: 'app-formacao-lista',
    standalone: true,
    imports: [CustomTableComponent, CardContent, BotaoAcoes, TagModule, CanDirective],
    templateUrl: './formacao-lista.component.html',
    styleUrl: './formacao-lista.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Listagem de formações (graduação + pós) da pessoa. */
export class FormacaoListaComponent implements OnInit {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    protected readonly roleFormacaoCriar = PERMISSOES.medicos.formacao.criar;
    rowClick = output<FormacaoListItem>();
    novoClick = output<'GRAD' | 'POS'>();

    loading = signal<boolean>(false);
    data = signal<FormacaoListItem[]>([]);
    dataTabela = signal<FormacaoListaRow[]>([]);
    diplomasMap = signal<Map<number, string>>(new Map());
    metadataTabela = {
        headers: {
            tipo: { type: 'tag', default: '-', hidden: false, value: 'Tipo' },
            instituicao: { type: 'text', default: '-', hidden: false, value: 'Instituição' },
            dataFormatura: { type: 'text', default: '-', hidden: false, value: 'Data Formatura' }
        },
        title: 'instituicao',
        tag: 'tipo',
        actions: {
            visualizar: {
                label: 'Visualizar',
                icon: 'pi pi-eye',
                color: 'success',
                command: (item: FormacaoListaRow) => this.handleRowClick(item.source)
            }
        }
    } satisfies TableMetadata;

    acoesNovo = signal<MenuItem[]>([
        {
            label: 'Nova Graduação',
            icon: 'pi pi-book',
            styleClass: 'bg-blue-500',
            command: () => this.novoClick.emit('GRAD')
        },
        {
            label: 'Nova Pós Graduação',
            icon: 'pi pi-graduation-cap',
            styleClass: 'bg-purple-500',
            command: () => this.novoClick.emit('POS')
        }
    ]);

    constructor(private formacaoService: FormacaoCoreService) {}

    ngOnInit(): void {
        this.carregarDados();
    }

    carregarDados() {
        const p = this.pessoa();
        if (!p) return;

        this.loading.set(true);
        forkJoin({
            grad: this.formacaoService.buscar(p),
            pos: this.formacaoService.listarPosGraduacoes(p)
        })
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (res) => {
                    const combined = [
                        ...res.grad.map(
                            (item, index) =>
                                ({
                                    ...item,
                                    _type: 'GRAD' as const,
                                    _index: index,
                                    table: {
                                        displayType: 'Graduação',
                                        displayInstitution: item.nmEscola || 'N/A',
                                        displayDate: item.dtFormatura || item.dtConclusaoCurso || 'N/A'
                                    }
                                }) as FormacaoListItem
                        ),
                        ...res.pos.map(
                            (item, index) =>
                                ({
                                    ...item,
                                    _type: 'POS' as const,
                                    _index: index,
                                    table: {
                                        displayType: 'Pós-Graduação',
                                        displayInstitution: item.nmEscola || 'N/A',
                                        displayDate: item.dtFormatura || item.dtFim || 'N/A'
                                    }
                                }) as FormacaoListItem
                        )
                    ];
                    this.data.set(combined);
                    this.dataTabela.set(combined.map((item) => this.mapearParaTabela(item)));

                    // Carregar os nomes dos tipos de diplomas/comprovantes dinamicamente
                    const uniqueTipoFormacaoIds = Array.from(new Set(res.grad.map((g) => g.tipoFormaIdTipoFormacao).filter((id) => id != null)));

                    const gradRequests = uniqueTipoFormacaoIds.map((id) => this.formacaoService.listarDiplomasPorTipoFormacao(id).pipe(catchError(() => of([]))));
                    const posRequest = this.formacaoService.listarComprovantePosGraduacao().pipe(catchError(() => of([])));

                    forkJoin([posRequest, ...gradRequests]).subscribe({
                        next: (responses) => {
                            const map = new Map<number, string>();
                            responses.forEach((list) => {
                                list.forEach((item: { idTipoFormacaoDiploma?: number; nmTipoDiploma?: string; idTipoDiploma?: number }) => {
                                    if (item.idTipoFormacaoDiploma != null && item.nmTipoDiploma) {
                                        map.set(Number(item.idTipoFormacaoDiploma), item.nmTipoDiploma);
                                    }
                                    if (item.idTipoDiploma != null && item.nmTipoDiploma) {
                                        map.set(Number(item.idTipoDiploma), item.nmTipoDiploma);
                                    }
                                });
                            });
                            this.diplomasMap.set(map);
                        }
                    });
                },
                error: (err) => {
                    this.logger.error('Erro ao carregar lista de formação', err);
                    this.data.set([]);
                    this.dataTabela.set([]);
                }
            });
    }

    handleRowClick(row: FormacaoListItem) {
        this.rowClick.emit({ ...row });
    }

    obterNomeCurso(item: FormacaoListItem): string {
        if (!item) return '';
        if (item._type === 'GRAD') {
            const grad = item as FormacaoRequest;
            return grad.nmCurso || 'Graduação';
        } else {
            const pos = item as PosGraduacaoResponse;
            return pos.nmCursoEspecializacao || 'Pós-Graduação';
        }
    }

    obterNomeCursoExibicao(item: FormacaoListItem): string {
        const nome = this.obterNomeCurso(item);
        return nome.length > 70 ? nome.substring(0, 70) + '...' : nome;
    }

    obterInstituicaoExibicao(item: FormacaoListItem): string {
        if (!item) return 'N/A';
        const inst = item.table?.displayInstitution || 'N/A';
        return inst.length > 70 ? inst.substring(0, 70) + '...' : inst;
    }

    obterComprovanteExibicao(item: FormacaoListItem): string {
        if (!item) return 'N/A';
        const id = item.idTipoFormacaoDiploma;
        if (id == null) return 'N/A';
        return this.diplomasMap().get(Number(id)) || 'N/A';
    }

    obterDataFormacaoExibicao(item: FormacaoListItem): string {
        if (!item) return 'N/A';
        return this.formatarData(item.dtFormatura);
    }

    obterExpDipRevalExibicao(item: FormacaoListItem): string {
        if (!item || item._type !== 'GRAD') return 'N/A';
        const grad = item as FormacaoRequest;
        return this.formatarData(grad.dtExpedicaoDiploma);
    }

    obterDtRegMecExibicao(item: FormacaoListItem): string {
        if (!item || item._type !== 'GRAD') return 'N/A';
        const grad = item as FormacaoRequest;
        return this.formatarData(grad.dtRegistroMec);
    }

    private mapearParaTabela(item: FormacaoListItem): FormacaoListaRow {
        return {
            tipo: {
                value: item.table?.displayType || '-',
                color: item._type === 'GRAD' ? 'var(--p-blue-500)' : 'var(--p-purple-500)'
            },
            instituicao: item.table?.displayInstitution || '-',
            dataFormatura: this.formatarData(item.table?.displayDate),
            source: item
        };
    }

    private formatarData(valor?: string): string {
        if (!valor || valor === 'N/A') return 'N/A';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        return valor;
    }
}
